/**
 * Track definitions.
 *
 * A track is pure data: a closed centreline of nodes plus optional surface
 * patches. The builder in `track.ts` converts this into (a) a physics grid of
 * surfaces, (b) checkpoint gates, (c) an AI racing line and (d) the render
 * description. That keeps artwork completely separate from the physics data —
 * nothing ever inspects rendered pixels to decide how much grip a car has.
 */

import type { SurfaceId } from './surfaces.js';

export interface TrackNode {
  x: number;
  y: number;
  /** Optional local road width override (defaults to the track's roadWidth). */
  w?: number;
}

export interface TrackPatch {
  surface: SurfaceId;
  shape: 'rect' | 'ellipse' | 'poly';
  x: number;
  y: number;
  w: number;
  h: number;
  angle?: number;
  /** Polygon points, relative to (x, y). Used when shape === 'poly'. */
  points?: Array<[number, number]>;
  /** When true the patch is a solid obstacle instead of a drivable surface. */
  solid?: boolean;
}

export interface TrackTheme {
  base: string;
  baseAlt: string;
  road: string;
  roadAlt: string;
  kerbA: string;
  kerbB: string;
  line: string;
  accent: string;
  treeColors: string[];
  buildingColors: string[];
  /** Ambient overlay colour and strength applied to the whole scene. */
  ambient?: { color: string; alpha: number };
  /** Scenery generation style. */
  decor: 'suburban' | 'forest' | 'desert' | 'harbour' | 'alpine' | 'neon';
}

export interface TrackDef {
  id: string;
  name: string;
  environment: string;
  description: string;
  /** 1 (easy) .. 5 (brutal) */
  difficulty: number;
  defaultLaps: number;
  width: number;
  height: number;
  /** Surface used everywhere that is not road, shoulder or a patch. */
  base: SurfaceId;
  /** Surface of the main road ribbon. */
  road: SurfaceId;
  /** Surface of the runoff strip that hugs the road. */
  shoulder: SurfaceId;
  /** Extra drivable width beyond the road on each side. */
  runoff: number;
  /** Road width in world px. */
  roadWidth: number;
  /** Painted kerb stripes along the road edge. */
  kerbs: boolean;
  /** When true, everything beyond road+runoff is solid barrier. */
  barriers: 'none' | 'edges';
  nodes: TrackNode[];
  patches?: TrackPatch[];
  checkpointCount: number;
  theme: TrackTheme;
  /** Starting grid direction is derived from the first nodes; this offsets it. */
  gridSpread: number;
}

const W = 2400;
const H = 1600;

export const TRACKS: TrackDef[] = [
  {
    id: 'sunny-acres',
    name: 'Sunny Acres',
    environment: 'Suburban Streets',
    description: 'Wide, flowing suburban roads with kerbed corners. The ideal place to learn the car.',
    difficulty: 1,
    defaultLaps: 3,
    width: W, height: H,
    base: 'grass', road: 'asphalt', shoulder: 'grass', runoff: 46, roadWidth: 168,
    kerbs: true, barriers: 'none',
    checkpointCount: 10, gridSpread: 34,
    nodes: [
      { x: 430, y: 340 }, { x: 1050, y: 260 }, { x: 1620, y: 330 }, { x: 1990, y: 600 },
      { x: 1960, y: 1010 }, { x: 1520, y: 1250 }, { x: 900, y: 1210 }, { x: 430, y: 950 },
    ],
    patches: [
      { surface: 'wet', shape: 'ellipse', x: 1210, y: 300, w: 260, h: 150, angle: -0.18 },
      { surface: 'boost', shape: 'rect', x: 1780, y: 300, w: 190, h: 60, angle: -1.05 },
    ],
    theme: {
      base: '#3f7a3a', baseAlt: '#356a32', road: '#4a4a55', roadAlt: '#44444e',
      kerbA: '#c8402f', kerbB: '#e8e4dc', line: '#e8dcae', accent: '#d9c27a',
      treeColors: ['#2f6b2c', '#3d8236', '#276027'],
      buildingColors: ['#c8b6a2', '#a89078', '#d8c8b0', '#8f7a68'],
      decor: 'suburban',
    },
  },
  {
    id: 'sakura-circuit',
    name: 'Sakura Circuit',
    environment: 'Professional Circuit',
    description: 'A purpose-built circuit with a long sweeper, a tight hairpin and a technical chicane.',
    difficulty: 2,
    defaultLaps: 3,
    width: W, height: H,
    base: 'grass', road: 'asphalt', shoulder: 'kerb', runoff: 26, roadWidth: 138,
    kerbs: true, barriers: 'edges',
    checkpointCount: 12, gridSpread: 30,
    nodes: [
      { x: 470, y: 430 }, { x: 1080, y: 380 }, { x: 1660, y: 420 }, { x: 2040, y: 660 },
      { x: 1940, y: 980 }, { x: 1500, y: 1080 }, { x: 1240, y: 830 }, { x: 990, y: 1090 },
      { x: 640, y: 1160 }, { x: 320, y: 880 },
    ],
    patches: [
      { surface: 'kerb', shape: 'rect', x: 1180, y: 1035, w: 40, h: 320, angle: 0.5 },
      { surface: 'gravel', shape: 'ellipse', x: 320, y: 700, w: 220, h: 220 },
      { surface: 'boost', shape: 'rect', x: 1200, y: 350, w: 200, h: 62, angle: -0.06 },
    ],
    theme: {
      base: '#3c7d43', baseAlt: '#33713b', road: '#544f55', roadAlt: '#4d484e',
      kerbA: '#d1443a', kerbB: '#f0ece2', line: '#f0ead2', accent: '#e8a0b8',
      treeColors: ['#2a6b34', '#358043', '#22602c'],
      buildingColors: ['#d8d2c8', '#b8b0a4', '#c0c8d0'],
      decor: 'suburban',
    },
  },
  {
    id: 'pinehill-rally',
    name: 'Pinehill Rally',
    environment: 'Forest Rally',
    description: 'Loose gravel through pine forest. No barriers — the trees will teach you the racing line.',
    difficulty: 3,
    defaultLaps: 3,
    width: W, height: H,
    base: 'grass', road: 'gravel', shoulder: 'dirt', runoff: 90, roadWidth: 116,
    kerbs: false, barriers: 'none',
    checkpointCount: 14, gridSpread: 30,
    nodes: [
      { x: 360, y: 380 }, { x: 1000, y: 280 }, { x: 1620, y: 400 }, { x: 2060, y: 680 },
      { x: 1940, y: 1060 }, { x: 1500, y: 1200 }, { x: 1330, y: 880 }, { x: 1080, y: 1210 },
      { x: 600, y: 1190 }, { x: 300, y: 820 },
    ],
    patches: [
      { surface: 'mud', shape: 'ellipse', x: 1500, y: 1180, w: 300, h: 170, angle: -0.2 },
      { surface: 'dirt', shape: 'ellipse', x: 900, y: 300, w: 380, h: 200 },
      { surface: 'mud', shape: 'ellipse', x: 480, y: 900, w: 260, h: 260 },
      { surface: 'boost', shape: 'rect', x: 1830, y: 500, w: 180, h: 60, angle: 0.9 },
    ],
    theme: {
      base: '#2f6033', baseAlt: '#27512b', road: '#8a7a5e', roadAlt: '#827156',
      kerbA: '#7a5a34', kerbB: '#9c8a68', line: '#b9a985', accent: '#5a4a2e',
      treeColors: ['#1f4a26', '#296030', '#16381c', '#2f6b34'],
      buildingColors: ['#6b5a44', '#7d6a52'],
      decor: 'forest',
    },
  },
  {
    id: 'duneside-sprint',
    name: 'Duneside Sprint',
    environment: 'Desert Circuit',
    description: 'Blistering heat, soft sand and long straights broken by boost pads.',
    difficulty: 3,
    defaultLaps: 3,
    width: W, height: H,
    base: 'sand', road: 'dirt', shoulder: 'sand', runoff: 70, roadWidth: 132,
    kerbs: false, barriers: 'none',
    checkpointCount: 10, gridSpread: 32,
    nodes: [
      { x: 320, y: 620 }, { x: 720, y: 310 }, { x: 1320, y: 270 }, { x: 1930, y: 360 },
      { x: 2120, y: 800 }, { x: 1880, y: 1200 }, { x: 1300, y: 1300 }, { x: 800, y: 1150 },
      { x: 430, y: 960 },
    ],
    patches: [
      { surface: 'boost', shape: 'rect', x: 1000, y: 285, w: 220, h: 68, angle: -0.03 },
      { surface: 'boost', shape: 'rect', x: 2040, y: 990, w: 200, h: 64, angle: 1.35 },
      { surface: 'mud', shape: 'ellipse', x: 1330, y: 1280, w: 300, h: 150 },
      { surface: 'dirt', shape: 'ellipse', x: 620, y: 400, w: 320, h: 220 },
    ],
    theme: {
      base: '#c9ab72', baseAlt: '#c0a168', road: '#9b7b52', roadAlt: '#93744c',
      kerbA: '#a97f4a', kerbB: '#d8c090', line: '#e3d3a8', accent: '#8a6a3c',
      treeColors: ['#6b7a3a', '#7d8a44'],
      buildingColors: ['#b09878', '#9c866a'],
      ambient: { color: '#ffd9a0', alpha: 0.10 },
      decor: 'desert',
    },
  },
  {
    id: 'dockside-loop',
    name: 'Dockside Loop',
    environment: 'Industrial Port',
    description: 'Narrow wet concrete between shipping containers. Oil slicks and blind corners everywhere.',
    difficulty: 4,
    defaultLaps: 4,
    width: W, height: H,
    base: 'gravel', road: 'wet', shoulder: 'gravel', runoff: 24, roadWidth: 120,
    kerbs: true, barriers: 'edges',
    checkpointCount: 12, gridSpread: 28,
    nodes: [
      { x: 420, y: 420 }, { x: 1020, y: 330 }, { x: 1620, y: 420 }, { x: 2010, y: 690 },
      { x: 1900, y: 1020 }, { x: 1420, y: 1090 }, { x: 900, y: 1010 }, { x: 610, y: 1210 },
      { x: 330, y: 900 },
    ],
    patches: [
      { surface: 'oil', shape: 'ellipse', x: 1500, y: 1080, w: 260, h: 120 },
      { surface: 'oil', shape: 'ellipse', x: 480, y: 700, w: 200, h: 200 },
      { surface: 'wet', shape: 'ellipse', x: 1200, y: 340, w: 400, h: 180 },
      { surface: 'boost', shape: 'rect', x: 1920, y: 640, w: 180, h: 60, angle: 1.0 },
      { surface: 'wall', shape: 'rect', x: 1150, y: 720, w: 60, h: 300, angle: 0, solid: true },
    ],
    theme: {
      base: '#5a5f66', baseAlt: '#51565d', road: '#4b5560', roadAlt: '#454f5a',
      kerbA: '#d8a12f', kerbB: '#3a4048', line: '#cfe4ff', accent: '#7f8a96',
      treeColors: ['#4a5a44'],
      buildingColors: ['#8d5a3c', '#3f6b8a', '#a8542f', '#6b7a4a'],
      ambient: { color: '#7fa0c0', alpha: 0.10 },
      decor: 'harbour',
    },
  },
  {
    id: 'frostpeak-pass',
    name: 'Frostpeak Pass',
    environment: 'Snowy Mountain',
    description: 'Frozen switchbacks with almost no grip. Brake early, steer gently, pray.',
    difficulty: 5,
    defaultLaps: 3,
    width: W, height: H,
    base: 'ice', road: 'wet', shoulder: 'ice', runoff: 40, roadWidth: 122,
    kerbs: false, barriers: 'edges',
    checkpointCount: 12, gridSpread: 28,
    nodes: [
      { x: 380, y: 520 }, { x: 820, y: 320 }, { x: 1320, y: 360 }, { x: 1660, y: 610 },
      { x: 1500, y: 870 }, { x: 1900, y: 1010 }, { x: 2040, y: 1260 }, { x: 1400, y: 1350 },
      { x: 880, y: 1210 }, { x: 480, y: 900 },
    ],
    patches: [
      { surface: 'ice', shape: 'ellipse', x: 1150, y: 390, w: 340, h: 180 },
      { surface: 'ice', shape: 'ellipse', x: 1730, y: 1150, w: 300, h: 200 },
      { surface: 'ice', shape: 'ellipse', x: 500, y: 800, w: 260, h: 220 },
      { surface: 'boost', shape: 'rect', x: 1650, y: 400, w: 180, h: 60, angle: -1.2 },
    ],
    theme: {
      base: '#e4edf5', baseAlt: '#d6e3ee', road: '#6d7a86', roadAlt: '#66727e',
      kerbA: '#c8402f', kerbB: '#f4f8fc', line: '#ffffff', accent: '#a8bccd',
      treeColors: ['#2f4a3a', '#264032'],
      buildingColors: ['#9c8a78', '#8a7a68'],
      ambient: { color: '#cfe4ff', alpha: 0.14 },
      decor: 'alpine',
    },
  },
  {
    id: 'neon-district',
    name: 'Neon District',
    environment: 'Neon City',
    description: 'Rain-slick downtown streets at midnight. Tight chicanes, hard walls, no margin.',
    difficulty: 5,
    defaultLaps: 4,
    width: W, height: H,
    base: 'asphalt', road: 'wet', shoulder: 'kerb', runoff: 18, roadWidth: 116,
    kerbs: true, barriers: 'edges',
    checkpointCount: 14, gridSpread: 26,
    nodes: [
      { x: 400, y: 400 }, { x: 1010, y: 300 }, { x: 1500, y: 460 }, { x: 1360, y: 760 },
      { x: 1900, y: 620 }, { x: 2100, y: 950 }, { x: 1700, y: 1180 }, { x: 1180, y: 1060 },
      { x: 940, y: 1280 }, { x: 520, y: 1150 }, { x: 300, y: 800 },
    ],
    patches: [
      { surface: 'wet', shape: 'ellipse', x: 1180, y: 500, w: 420, h: 300 },
      { surface: 'oil', shape: 'ellipse', x: 1550, y: 1150, w: 240, h: 140 },
      { surface: 'oil', shape: 'ellipse', x: 620, y: 1000, w: 220, h: 180 },
      { surface: 'boost', shape: 'rect', x: 1960, y: 760, w: 170, h: 58, angle: 1.25 },
      { surface: 'wall', shape: 'rect', x: 1150, y: 500, w: 54, h: 260, angle: 0, solid: true },
    ],
    theme: {
      base: '#22242e', baseAlt: '#1c1e26', road: '#3a3f4d', roadAlt: '#343948',
      kerbA: '#ff2f8e', kerbB: '#22e0ff', line: '#8fe8ff', accent: '#ff8ad8',
      treeColors: ['#2a4a4a'],
      buildingColors: ['#2c3244', '#3a2f4a', '#243a44', '#41304f'],
      ambient: { color: '#3a2a6a', alpha: 0.16 },
      decor: 'neon',
    },
  },
];

export const TRACK_BY_ID: Record<string, TrackDef> = TRACKS.reduce(
  (acc, t) => {
    acc[t.id] = t;
    return acc;
  },
  {} as Record<string, TrackDef>,
);

export function getTrack(id: string): TrackDef {
  const t = TRACK_BY_ID[id];
  if (!t) throw new Error(`Unknown track id: ${id}`);
  return t;
}
